/* PlayMaster IA — IndexedDB helper.
 *
 * Lightweight wrapper over the native IndexedDB API for offline storage.
 * We don't pull in a dependency like idb to keep the bundle small.
 *
 * Stores:
 *   - plays       (key: id)                — full play documents including enrichments
 *   - profile     (key: 'me')              — user profile snapshot
 *   - kv          (key: string)            — arbitrary string blobs (jwt, timestamps, etc.)
 *   - outbox      (key auto, by createdAt) — queued write operations to retry when online
 */

const DB_NAME = 'playmaster-offline';
const DB_VERSION = 1;

function _open() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains('plays')) db.createObjectStore('plays', { keyPath: 'id' });
      if (!db.objectStoreNames.contains('profile')) db.createObjectStore('profile', { keyPath: 'key' });
      if (!db.objectStoreNames.contains('kv')) db.createObjectStore('kv', { keyPath: 'key' });
      if (!db.objectStoreNames.contains('outbox')) {
        const s = db.createObjectStore('outbox', { keyPath: 'id', autoIncrement: true });
        s.createIndex('createdAt', 'createdAt', { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function _txn(storeName, mode = 'readonly') {
  return _open().then((db) => {
    const tx = db.transaction(storeName, mode);
    return [tx.objectStore(storeName), tx];
  });
}

function _wrap(req) {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// ----- KV (simple key-value) -----
export async function kvSet(key, value) {
  const [store] = await _txn('kv', 'readwrite');
  await _wrap(store.put({ key, value, at: Date.now() }));
}
export async function kvGet(key) {
  const [store] = await _txn('kv');
  const row = await _wrap(store.get(key));
  return row?.value ?? null;
}
export async function kvDel(key) {
  const [store] = await _txn('kv', 'readwrite');
  await _wrap(store.delete(key));
}

// ----- Profile -----
export async function setProfile(p) {
  if (!p) return;
  const [store] = await _txn('profile', 'readwrite');
  await _wrap(store.put({ key: 'me', value: p, at: Date.now() }));
}
export async function getProfile() {
  const [store] = await _txn('profile');
  const row = await _wrap(store.get('me'));
  return row?.value ?? null;
}

// ----- Plays cache -----
export async function putPlay(play) {
  if (!play?.id) return;
  const [store] = await _txn('plays', 'readwrite');
  await _wrap(store.put({ ...play, _cachedAt: Date.now() }));
}
export async function putPlays(plays = []) {
  const [store, tx] = await _txn('plays', 'readwrite');
  for (const p of plays) {
    if (p?.id) store.put({ ...p, _cachedAt: Date.now() });
  }
  return new Promise((resolve) => { tx.oncomplete = () => resolve(); });
}
export async function getPlay(id) {
  const [store] = await _txn('plays');
  return _wrap(store.get(id));
}
export async function getAllPlays() {
  const [store] = await _txn('plays');
  return _wrap(store.getAll());
}

// ----- Outbox (write queue for offline POST/PUT/DELETE) -----
export async function enqueue(op) {
  const [store] = await _txn('outbox', 'readwrite');
  await _wrap(store.add({ ...op, createdAt: Date.now(), tries: 0 }));
}
export async function listQueue() {
  const [store] = await _txn('outbox');
  return _wrap(store.getAll());
}
export async function removeFromQueue(id) {
  const [store] = await _txn('outbox', 'readwrite');
  await _wrap(store.delete(id));
}
export async function bumpQueueTries(id) {
  const [store] = await _txn('outbox', 'readwrite');
  const row = await _wrap(store.get(id));
  if (row) {
    row.tries = (row.tries || 0) + 1;
    row.lastTryAt = Date.now();
    await _wrap(store.put(row));
  }
}

export async function clearOfflineData() {
  for (const name of ['plays', 'profile', 'kv', 'outbox']) {
    const [store] = await _txn(name, 'readwrite');
    await _wrap(store.clear());
  }
}
